import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import DeleteConfirmModal from "components/ui/DeleteConfirmModal";
import InvoiceFormModal from "./components/InvoiceFormModal";
import { invoicesApi } from "api/invoices";
import { useToast } from "components/ui/Toast";
import { getCurrentUser } from "api/client";

function formatMoney(value) {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  return `₹${Number(value).toLocaleString("en-IN")}`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN");
}

const STATUS_CONFIG = {
  draft: { bg: "#F3F4F6", color: "#4B5563", label: "Draft" },
  sent: { bg: "#DBEAFE", color: "#1D4ED8", label: "Sent" },
  paid: { bg: "#DCFCE7", color: "#065F46", label: "Paid" },
  overdue: { bg: "#FEE2E2", color: "#B91C1C", label: "Overdue" },
  cancelled: { bg: "#FEF3C7", color: "#92400E", label: "Cancelled" },
};

export default function Invoices() {
  const currentUser = getCurrentUser();
  const isManager = currentUser?.role === "Owner" || currentUser?.role === "Manager";
  const toast = useToast();
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const load = async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const items = await invoicesApi.getAll({ status: statusFilter, q: searchQuery || undefined });
      setInvoices(items);
    } catch (error) {
      setLoadError(error?.message || "Unable to load invoices.");
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  // Client-side search already handled server-side via load(); keep debounced effect for manual search button? For now re-load on search submit.
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const handleSubmit = async (payload) => {
    try {
      if (selectedInvoice?.id) {
        await invoicesApi.update(selectedInvoice.id, payload);
        toast.success("Invoice updated.");
      } else {
        await invoicesApi.create(payload);
        toast.success("Invoice created.");
      }
      setIsFormOpen(false);
      setSelectedInvoice(null);
      await load();
    } catch (error) {
      toast.error(error?.message || "Could not save the invoice.");
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await invoicesApi.remove(deleteTargetId);
      toast.success("Invoice deleted.");
      setIsDeleteOpen(false);
      setDeleteTargetId(null);
      await load();
    } catch (error) {
      toast.error(error?.message || "Could not delete the invoice.");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await invoicesApi.update(id, { status });
      toast.success(`Invoice marked ${status}.`);
      await load();
    } catch (error) {
      toast.error(error?.message || "Could not update status.");
    }
  };

  const stats = useMemo(() => {
    const total = invoices.length;
    const paid = invoices.filter((inv) => inv.status === "paid").length;
    const overdue = invoices.filter((inv) => inv.status === "overdue").length;
    const totalValue = invoices.reduce((s, inv) => s + (inv.amountIncTax || 0), 0);
    return { total, paid, overdue, totalValue };
  }, [invoices]);

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
              Invoices
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
              Basic invoice management — amounts in ₹, tax, and status lifecycle
            </p>
          </div>
          <Button variant="default" size="sm" iconName="Plus" iconPosition="left" iconSize={14} onClick={() => { setSelectedInvoice(null); setIsFormOpen(true); }}>
            New Invoice
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-xs uppercase tracking-wide" style={{ color: "var(--color-text-secondary)" }}>Total</p>
            <p className="font-heading font-bold text-xl mt-1" style={{ color: "var(--color-foreground)" }}>{stats.total}</p>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-xs uppercase tracking-wide" style={{ color: "var(--color-text-secondary)" }}>Paid</p>
            <p className="font-heading font-bold text-xl mt-1" style={{ color: "#065F46" }}>{stats.paid}</p>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-xs uppercase tracking-wide" style={{ color: "var(--color-text-secondary)" }}>Overdue</p>
            <p className="font-heading font-bold text-xl mt-1" style={{ color: "#B91C1C" }}>{stats.overdue}</p>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-xs uppercase tracking-wide" style={{ color: "var(--color-text-secondary)" }}>Total Value</p>
            <p className="font-heading font-bold text-xl mt-1" style={{ color: "var(--color-foreground)" }}>{formatMoney(stats.totalValue)}</p>
          </div>
        </div>

        {/* Search + filter */}
        <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Icon name="Search" size={16} color="var(--color-text-secondary)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by number, customer, company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-lg border font-caption text-sm"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)", outline: "none" }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 rounded-lg border font-caption text-sm"
            style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)" }}
          >
            <option value="all">All Statuses</option>
            {Object.keys(STATUS_CONFIG).map((s) => (
              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
            ))}
          </select>
          <Button type="submit" variant="outline" size="sm" iconName="Search" iconPosition="left">Search</Button>
        </form>

        {loadError && (
          <div className="mb-4 rounded-lg border px-4 py-3 flex items-center justify-between gap-4" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{loadError}</p>
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" onClick={load}>Retry</Button>
          </div>
        )}

        {isLoading ? (
          <div className="rounded-xl border px-4 py-16 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading invoices...</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="rounded-xl border px-4 py-16 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <Icon name="Receipt" size={32} color="var(--color-text-secondary)" className="mx-auto mb-2" />
            <p className="font-caption text-sm mb-2" style={{ color: "var(--color-text-secondary)" }}>No invoices yet</p>
            <Button variant="default" size="sm" iconName="Plus" iconPosition="left" onClick={() => { setSelectedInvoice(null); setIsFormOpen(true); }}>Create your first invoice</Button>
          </div>
        ) : (
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: 800 }}>
                <thead>
                  <tr style={{ background: "var(--color-muted)", borderBottom: "1px solid var(--color-border)" }}>
                    <th className="px-4 py-3 text-left font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Number</th>
                    <th className="px-4 py-3 text-left font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Customer</th>
                    <th className="px-4 py-3 text-right font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Amount (inc. tax)</th>
                    <th className="px-4 py-3 text-left font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Status</th>
                    <th className="px-4 py-3 text-left font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Due</th>
                    <th className="px-4 py-3 text-right font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, idx) => {
                    const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                    return (
                      <tr key={inv.id} style={{ borderTop: "1px solid var(--color-border)", background: idx % 2 === 0 ? "transparent" : "var(--color-muted)" }}>
                        <td className="px-4 py-3 font-caption text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>{inv.invoiceNumber}</td>
                        <td className="px-4 py-3 font-caption text-sm" style={{ color: "var(--color-foreground)" }}>{inv.contactName || inv.companyName || "—"}</td>
                        <td className="px-4 py-3 font-caption text-sm text-right font-semibold" style={{ color: "var(--color-foreground)" }}>{formatMoney(inv.amountIncTax)} <span className="font-normal" style={{ color: "var(--color-text-secondary)" }}>({inv.taxPercent}% GST)</span></td>
                        <td className="px-4 py-3"><span className="px-2.5 py-1 rounded-full font-caption text-xs font-semibold" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span></td>
                        <td className="px-4 py-3 font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>{formatDate(inv.dueDateUtc)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {inv.status !== "paid" && inv.status !== "cancelled" && (
                              <button onClick={() => handleStatusChange(inv.id, "paid")} className="px-2 py-1 rounded-md font-caption text-xs font-medium hover:bg-muted" style={{ color: "#065F46" }} title="Mark paid">Paid</button>
                            )}
                            {inv.status === "draft" && (
                              <button onClick={() => handleStatusChange(inv.id, "sent")} className="px-2 py-1 rounded-md font-caption text-xs font-medium hover:bg-muted" style={{ color: "#1D4ED8" }} title="Mark sent">Sent</button>
                            )}
                            <button onClick={() => { setSelectedInvoice(inv); setIsFormOpen(true); }} className="flex items-center justify-center rounded-md hover:bg-muted" style={{ width: 28, height: 28, color: "var(--color-text-secondary)" }} title="Edit"><Icon name="Edit2" size={13} color="currentColor" /></button>
                            {isManager && (
                              <button onClick={() => { setDeleteTargetId(inv.id); setIsDeleteOpen(true); }} className="flex items-center justify-center rounded-md hover:bg-red-50" style={{ width: 28, height: 28, color: "#DC2626" }} title="Delete"><Icon name="Trash2" size={13} color="currentColor" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <InvoiceFormModal isOpen={isFormOpen} invoice={selectedInvoice} onClose={() => { setIsFormOpen(false); setSelectedInvoice(null); }} onSubmit={handleSubmit} />
        <DeleteConfirmModal isOpen={isDeleteOpen} itemName="Invoice" onClose={() => { setIsDeleteOpen(false); setDeleteTargetId(null); }} onConfirm={handleDelete} />
      </div>
    </AppLayout>
  );
}